import React from 'react';
import { Helmet } from 'react-helmet-async';

// ── NightClub Business Schema ─────────────────────────────────────────────────
export function NightClubSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": ["NightClub", "BarOrPub", "FoodEstablishment"],
    "@id": "https://scratchjsr.com/#nightclub",
    "name": "Scratch Unleashed",
    "alternateName": "Scratch Unleashed Bar & Kitchen",
    "description": "Premium nightclub and party venue in Jamshedpur offering world-class DJ nights, VIP tables, bottle service, birthday party packages, and unforgettable nightlife experiences. Jharkhand's best night club.",
    "url": "https://scratchjsr.com",
    "logo": {
      "@type": "ImageObject",
      "url": "https://scratchjsr.com/images/scratch-logo.png",
      "width": 512,
      "height": 512
    },
    "image": [
      "https://scratchjsr.com/images/gallery-1.jpg",
      "https://scratchjsr.com/images/gallery-2.webp",
      "https://scratchjsr.com/images/gallery-3.webp",
      "https://scratchjsr.com/images/gallery-4.webp",
      "https://scratchjsr.com/images/gallery-5.webp"
    ],
    "telephone": "+917870369666",
    "priceRange": "₹₹₹",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "3rd Floor, Bindal Mall, Sonari",
      "addressLocality": "Jamshedpur",
      "addressRegion": "Jharkhand",
      "postalCode": "831011",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "22.8059",
      "longitude": "86.1979"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Friday", "Saturday", "Sunday"],
        "opens": "18:00",
        "closes": "03:00"
      }
    ],
    "servesCuisine": ["Bar", "Cocktails", "Premium Spirits"],
    "hasMenu": "https://scratchjsr.com/#menu",
    "acceptsReservations": "True",
    "paymentAccepted": "Cash, Credit Card, Debit Card, UPI",
    "currenciesAccepted": "INR",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.6",
      "reviewCount": "312",
      "bestRating": "5",
      "worstRating": "1"
    },
    "amenityFeature": [
      { "@type": "LocationFeatureSpecification", "name": "VIP Tables", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Bottle Service", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "DJ Booth", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Dance Floor", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Parking", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Funktion-One Sound System", "value": true }
    ],
    "areaServed": [
      { "@type": "City", "name": "Jamshedpur", "@id": "https://en.wikipedia.org/wiki/Jamshedpur" },
      { "@type": "State", "name": "Jharkhand" }
    ],
    "sameAs": [
      "https://www.instagram.com/scratchunleashed",
      "https://www.facebook.com/scratchunleashed"
    ],
    "keywords": "night club Jamshedpur, bar Jamshedpur, DJ night Jamshedpur, VIP table Jamshedpur, birthday party venue Jamshedpur, best nightclub Jharkhand"
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

// ── WebSite Schema (helps AI understand the site) ────────────────────────────
export function WebSiteSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://scratchjsr.com/#website",
    "name": "Scratch Unleashed",
    "description": "Official website of Scratch Unleashed - Jamshedpur's premier night club, bar and party venue at Bindal Mall, Sonari.",
    "url": "https://scratchjsr.com",
    "inLanguage": "en-IN",
    "publisher": {
      "@id": "https://scratchjsr.com/#nightclub"
    },
    "potentialAction": {
      "@type": "ReserveAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://scratchjsr.com/checkin"
      },
      "name": "Reserve Table"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

// ── Event Schema ─────────────────────────────────────────────────────────────
export function EventSchema({ event }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "MusicEvent",
    "name": event.title,
    "description": event.description || `${event.title} at Scratch Unleashed, Jamshedpur`,
    "startDate": event.start_date,
    "endDate": event.end_date || event.start_date,
    "eventStatus": "https://schema.org/EventScheduled",
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "location": {
      "@type": "Place",
      "name": "Scratch Unleashed",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "3rd Floor, Bindal Mall, Sonari",
        "addressLocality": "Jamshedpur",
        "addressRegion": "Jharkhand",
        "postalCode": "831011",
        "addressCountry": "IN"
      }
    },
    "image": event.banner_image || "https://scratchjsr.com/images/gallery-2.webp",
    "offers": {
      "@type": "Offer",
      "price": event.cover_charge || 0,
      "priceCurrency": "INR",
      "availability": "https://schema.org/InStock",
      "url": `https://scratchjsr.com/checkin`,
      "validFrom": new Date().toISOString()
    },
    "organizer": {
      "@type": "Organization",
      "name": "Scratch Unleashed",
      "url": "https://scratchjsr.com"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

// ── Article/Blog Post Schema ─────────────────────────────────────────────────
export function ArticleSchema({ post }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.excerpt,
    "image": {
      "@type": "ImageObject",
      "url": post.image || "https://scratchjsr.com/images/gallery-1.jpg",
      "width": 800,
      "height": 450
    },
    "author": {
      "@type": "Organization",
      "name": "Scratch Unleashed",
      "url": "https://scratchjsr.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Scratch Unleashed",
      "logo": {
        "@type": "ImageObject",
        "url": "https://scratchjsr.com/images/scratch-logo.png"
      }
    },
    "datePublished": post.publishDate,
    "dateModified": post.modifiedDate || post.publishDate,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://scratchjsr.com/blog/${post.slug}`
    },
    "keywords": post.keywords,
    "articleSection": post.category,
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": ["h1", "h2", ".article-excerpt"]
    },
    "about": {
      "@id": "https://scratchjsr.com/#nightclub"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

// ── FAQ Schema ───────────────────────────────────────────────────────────────
export function FAQSchema({ faqs }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

// ── Breadcrumb Schema ────────────────────────────────────────────────────────
export function BreadcrumbSchema({ items }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

// ── LocalBusiness + Service Schema for landing pages ────────────────────────
export function ServiceSchema({ serviceName, serviceDescription, serviceUrl }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": serviceName,
    "description": serviceDescription,
    "url": serviceUrl,
    "provider": {
      "@id": "https://scratchjsr.com/#nightclub"
    },
    "areaServed": {
      "@type": "City",
      "name": "Jamshedpur"
    },
    "availableChannel": {
      "@type": "ServiceChannel",
      "serviceUrl": "https://scratchjsr.com/checkin",
      "serviceSmsNumber": "+917870369666"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}
